/**
 * Detalhe da sobra + ações de transição (máquina de estados §6):
 * disponível → reservada → usada; disponível ↔ indisponível; qualquer → descartada.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiEdit2 } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  definirDisponibilidadeSobra, descartarSobra, desmarcarUsoSobra, ESTADO_SOBRA_LABEL,
  ESTADO_SOBRA_TONE, liberarSobra, moverSobraParaBox, obterSobra, reservarSobra, usarSobra,
} from "../../../services/suprimentos";
import { nomeMaterialDaSobra } from "../../../utils/nomeMaterial";
import { fmtData, fmtMoeda } from "../helpers";
import { Block, FieldLabel, FieldRow, FieldValue, Pill, SecondaryButton, SectionTitle, Select } from "../styles";
import { Acoes } from "./styles";
import { fmtMm } from "./TabelaSobras";
import { Carregando } from "../_shared/Estados";

function dataHora(iso) {
  return iso ? new Date(iso).toLocaleString("pt-BR") : "—";
}

export default function SobraDetalhePanel({ sobraId, boxes, onClose, onMudou, onEditar, versao = 0 }) {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const podeStatus = isMaster || hasAllPermissions(["sobra:change_status"]);
  const podeMover = isMaster || hasAllPermissions(["sobra:edit"]);
  const [sobra, setSobra] = useState(null);

  const carregar = useCallback(async () => {
    if (!sobraId) { setSobra(null); return; }
    setSobra(await obterSobra(request, sobraId));
  }, [request, sobraId]);

  // `versao` sobe quando a edição salva: recarrega sem fechar o painel.
  useEffect(() => { carregar().catch(() => setSobra(null)); }, [carregar, versao]);

  const acao = (fn) => async () => {
    const atualizada = await fn();
    setSobra(atualizada);
    await onMudou();
  };

  const pedirRef = (rotulo) => {
    const ref = window.prompt(rotulo, "");
    return ref === null ? null : ref.trim();
  };

  const moverBox = async (boxId) => {
    setSobra(await moverSobraParaBox(request, sobra.id, boxId));
    await onMudou();
  };

  const e = sobra?.estado;
  return (
    <SlideOverPanel
      open={Boolean(sobraId)}
      onClose={onClose}
      title={sobra?.codigo || "Sobra"}
      subtitle={sobra ? `${nomeMaterialDaSobra(sobra)} · ${sobra.produto_codigo}` : ""}
      width="min(94vw, 640px)"
    >
      {!sobra ? (
        <Carregando linhas={3} colunas={2} />
      ) : (
        <>
          <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Pill $tone={ESTADO_SOBRA_TONE[e] || "neutral"}>{ESTADO_SOBRA_LABEL[e] || e}</Pill>
            {!sobra.disponivel_para_uso && e === "disponivel" ? (
              <Pill $tone="neutral">oculta do otimizador</Pill>
            ) : null}
            {podeMover && onEditar && !["usada", "descartada"].includes(e) ? (
              <SecondaryButton type="button" style={{ marginLeft: "auto" }} onClick={() => onEditar(sobra)}>
                <FiEdit2 size={13} /> Editar
              </SecondaryButton>
            ) : null}
          </div>
          <Block>
            <FieldRow><FieldLabel>Medidas</FieldLabel><FieldValue>{fmtMm(sobra.comprimento_mm)} × {fmtMm(sobra.largura_mm)} × {fmtMm(sobra.espessura_mm)} mm</FieldValue></FieldRow>
            <FieldRow><FieldLabel>Área</FieldLabel><FieldValue>{Number(sobra.area_m2).toFixed(4)} m²</FieldValue></FieldRow>
            <FieldRow><FieldLabel>Custo estimado</FieldLabel><FieldValue>{fmtMoeda(sobra.custo_estimado)}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>Veio</FieldLabel><FieldValue>{sobra.tem_veio === null ? "—" : sobra.tem_veio ? "Sim" : "Não"}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>Origem</FieldLabel><FieldValue>{sobra.origem_display || sobra.origem}{sobra.origem_ref ? ` · ${sobra.origem_ref}` : ""}</FieldValue></FieldRow>
            <FieldRow>
              <FieldLabel>Box</FieldLabel>
              <FieldValue>
                {podeMover && !["usada", "descartada"].includes(e) ? (
                  <Select value={sobra.box || ""} onChange={(ev) => moverBox(ev.target.value || null)}>
                    <option value="">— Sem box —</option>
                    {boxes.filter((b) => b.ativo || b.id === sobra.box).map((b) => (
                      <option key={b.id} value={b.id}>{b.codigo}{b.nome ? ` — ${b.nome}` : ""}</option>
                    ))}
                  </Select>
                ) : (sobra.box_codigo || "—")}
              </FieldValue>
            </FieldRow>
            {sobra.reservada_por_ref ? <FieldRow><FieldLabel>Reservada por</FieldLabel><FieldValue>{sobra.reservada_por_ref} · {dataHora(sobra.reservada_em)}</FieldValue></FieldRow> : null}
            {sobra.usada_por_ref ? <FieldRow><FieldLabel>Usada em</FieldLabel><FieldValue>{sobra.usada_por_ref} · {dataHora(sobra.usada_em)}</FieldValue></FieldRow> : null}
            {sobra.descartada_em ? <FieldRow><FieldLabel>Descartada</FieldLabel><FieldValue>{dataHora(sobra.descartada_em)} — {sobra.descartada_motivo || "sem motivo"}</FieldValue></FieldRow> : null}
            {sobra.observacao ? <FieldRow><FieldLabel>Observação</FieldLabel><FieldValue>{sobra.observacao}</FieldValue></FieldRow> : null}
          </Block>

          {podeStatus && (
            <Acoes>
              {e === "disponivel" && (
                <>
                  <AsyncButton variant="primary" successMessage="Sobra reservada." onClick={acao(async () => {
                    const ref = pedirRef("Reservar para (lote, pedido ou pessoa):");
                    if (ref === null) return sobra;
                    return reservarSobra(request, sobra.id, ref);
                  })}>Reservar</AsyncButton>
                  <AsyncButton variant="secondary" successMessage="Sobra marcada como usada." onClick={acao(async () => {
                    const ref = pedirRef("Usada em (lote/pedido):");
                    if (ref === null) return sobra;
                    return usarSobra(request, sobra.id, ref);
                  })}>Marcar usada</AsyncButton>
                  <AsyncButton variant="secondary" silentSuccess onClick={acao(() => definirDisponibilidadeSobra(request, sobra.id, !sobra.disponivel_para_uso))}>
                    {sobra.disponivel_para_uso ? "Ocultar do otimizador" : "Oferecer ao otimizador"}
                  </AsyncButton>
                </>
              )}
              {e === "reservada" && (
                <>
                  <AsyncButton variant="secondary" successMessage="Reserva liberada." onClick={acao(() => liberarSobra(request, sobra.id))}>Liberar reserva</AsyncButton>
                  <AsyncButton variant="primary" successMessage="Sobra marcada como usada." onClick={acao(() => usarSobra(request, sobra.id, sobra.reservada_por_ref || ""))}>Marcar usada</AsyncButton>
                </>
              )}
              {e === "usada" && (
                <AsyncButton variant="secondary" successMessage="Uso desfeito." onClick={acao(() => desmarcarUsoSobra(request, sobra.id))}>Desfazer uso</AsyncButton>
              )}
              {e === "indisponivel" && (
                <AsyncButton variant="primary" successMessage="Sobra disponível." onClick={acao(() => definirDisponibilidadeSobra(request, sobra.id, true))}>Tornar disponível</AsyncButton>
              )}
              {!["usada", "descartada"].includes(e) && (
                <AsyncButton variant="danger" successMessage="Sobra descartada." onClick={acao(async () => {
                  const motivo = pedirRef("Motivo do descarte:");
                  if (motivo === null) return sobra;
                  return descartarSobra(request, sobra.id, motivo);
                })}>Descartar</AsyncButton>
              )}
            </Acoes>
          )}

          {(sobra.eventos || []).length > 0 && (
            <Block style={{ marginTop: 16 }}>
              <SectionTitle>Histórico</SectionTitle>
              {sobra.eventos.map((ev) => (
                <FieldRow key={ev.id}>
                  <FieldLabel>{fmtData(ev.created_at)}</FieldLabel>
                  <FieldValue>
                    {ev.evento}{ev.de_estado ? ` (${ev.de_estado} → ${ev.para_estado})` : ""}
                    {ev.ator_nome ? ` · ${ev.ator_nome}` : ""}
                  </FieldValue>
                </FieldRow>
              ))}
            </Block>
          )}
        </>
      )}
    </SlideOverPanel>
  );
}
